import React, { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { portaleAPI } from '../utils/api';
import toast from 'react-hot-toast';

interface ProfileFormData {
  telefono: string;
  email: string;
  indirizzo: string;
  citta: string;
  provincia: string;
  cap: string;
}

const CustomerProfile: React.FC = () => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['customer-profile'],
    queryFn: () => portaleAPI.getProfile(),
  });

  const profile = data?.data;

  const { register, handleSubmit, reset, formState: { isDirty, isSubmitting } } = useForm<ProfileFormData>();

  useEffect(() => {
    if (profile) {
      reset({
        telefono: profile.telefono || '',
        email: profile.email || '',
        indirizzo: profile.indirizzo || '',
        citta: profile.citta || '',
        provincia: profile.provincia || '',
        cap: profile.cap || '',
      });
    }
  }, [profile, reset]);

  const updateMutation = useMutation({
    mutationFn: (data: ProfileFormData) => portaleAPI.updateProfile(data),
    onSuccess: () => {
      toast.success('Profilo aggiornato');
      queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    },
  });

  const onSubmit = (data: ProfileFormData) => {
    updateMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse bg-gray-200 h-8 w-48 rounded" />
        <div className="animate-pulse bg-gray-200 h-96 rounded-lg" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="bg-white shadow rounded-lg p-8 text-center">
        <h3 className="text-lg font-medium text-gray-900">Profilo non disponibile</h3>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Il Mio Profilo</h1>
        <p className="text-gray-600 mt-1">Visualizza e modifica i tuoi dati di contatto</p>
      </div>

      {/* Company Info - Read Only */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Dati Aziendali</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Ragione Sociale</span>
            <p className="font-medium text-gray-900">
              {profile.ragione_sociale || `${profile.nome} ${profile.cognome || ''}`}
            </p>
          </div>
          <div>
            <span className="text-gray-500">Tipo</span>
            <p className="font-medium text-gray-900">
              {profile.tipo === 'azienda' ? 'Azienda' : 'Persona Fisica'}
            </p>
          </div>
          {profile.partita_iva && (
            <div>
              <span className="text-gray-500">Partita IVA</span>
              <p className="font-medium text-gray-900">{profile.partita_iva}</p>
            </div>
          )}
          {profile.codice_fiscale && (
            <div>
              <span className="text-gray-500">Codice Fiscale</span>
              <p className="font-medium text-gray-900">{profile.codice_fiscale}</p>
            </div>
          )}
        </div>
      </div>

      {/* Contact Info - Editable */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Dati di Contatto</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefono
              </label>
              <input
                type="text"
                {...register('telefono')}
                className="block w-full border border-gray-300 rounded-md text-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                {...register('email')}
                className="block w-full border border-gray-300 rounded-md text-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Indirizzo
              </label>
              <input
                type="text"
                {...register('indirizzo')}
                className="block w-full border border-gray-300 rounded-md text-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Città
              </label>
              <input
                type="text"
                {...register('citta')}
                className="block w-full border border-gray-300 rounded-md text-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Provincia
                </label>
                <input
                  type="text"
                  {...register('provincia')}
                  className="block w-full border border-gray-300 rounded-md text-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CAP
                </label>
                <input
                  type="text"
                  {...register('cap')}
                  className="block w-full border border-gray-300 rounded-md text-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={!isDirty || isSubmitting || updateMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {updateMutation.isPending ? 'Salvataggio...' : 'Salva Modifiche'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CustomerProfile;
